import { Box, Button, Slider, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch } from 'store/store';
import { getIsPaymentMode, getIsTicketUpdate } from 'store/ticket-service/selector';
import { setPaymentMode } from 'store/ticket-service/ticketSlice';
import { getStripeConnection } from 'store/user-service/selector';
import { setStripeConnection, triggerTicketSave } from 'store/user-service/userSlice';

import { NamespacesList } from 'core/enums/common';
import { createStripePayment } from 'core/helpers/stripeUtilities';
import { useGetConnectionId } from 'core/hooks/useGetConnectionId';

import { SharedModal } from 'components/shared/Modal';

import { titleTextColor } from 'assets/styles/_palette';

const defaultSliderValue = 1;
const defaultDocumentPrice = 9.99;
const minSliderValue = 0;
const maxSliderValue = 10;
const sliderStep = 1;

export const PaymentModal = () => {
	const { t: paymentTranslate } = useTranslation(NamespacesList['payment']);

	const dispatch = useDispatch<AppDispatch>();

	const paymentMode = useSelector(getIsPaymentMode);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const connectingStripe = useSelector(getStripeConnection);
	const [sliderValue, setSliderValue] = useState<number>(defaultSliderValue);

	const { connectionId } = useGetConnectionId();

	const sliderLabelText = (value: number) => {
		return `${value} ${paymentTranslate('text.documents')}`;
	};

	const handlePayment = (amountOfDocuments: number) => {
		dispatch(setStripeConnection(true));

		if (isTicketUpdate) dispatch(triggerTicketSave());

		if (connectionId) {
			createStripePayment({
				connectionId,
				amountOfDocuments,
			});
		}
	};

	const leavePaymentPage = () => {
		dispatch(setPaymentMode(false));
	};

	return (
		<SharedModal isOpen={paymentMode} handleClose={leavePaymentPage}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					alignSelf: 'center',
					width: { xs: '100%', sm: '438px' },
					margin: '48px 0',
				}}
			>
				<Typography variant="h3" sx={{ fontSize: '24px', fontWeight: '700', marginTop: '8px' }}>
					{paymentTranslate('text.title')}
				</Typography>

				<Typography sx={{ color: '#9FA6B3', marginTop: '8px', textAlign: 'center' }}>
					{paymentTranslate('text.description')}
				</Typography>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						width: '100%',
						margin: '48px 0 8px',
					}}
				>
					<Slider
						value={sliderValue}
						aria-label={paymentTranslate('slider.label') || ''}
						defaultValue={defaultSliderValue}
						getAriaValueText={sliderLabelText}
						valueLabelFormat={sliderLabelText}
						valueLabelDisplay="auto"
						step={sliderStep}
						min={minSliderValue}
						max={maxSliderValue}
						onChange={(_event, newValue) =>
							setSliderValue(typeof newValue === 'number' ? newValue : newValue[0])
						}
						sx={{
							color: '#4786FF',
							'& .MuiSlider-thumb': {
								backgroundColor: '#4786FF',
								border: '6px solid #ffffff',
								boxShadow: '0px 4px 8px 0px #4573871F, 0px -1px 8px 0px #43687C14',
							},
							'& .MuiSlider-rail': {
								backgroundColor: '#E7E8EC',
							},
						}}
					/>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
						}}
					>
						<Typography
							sx={{
								fontSize: '16px',
								fontWeight: '700',
								color: titleTextColor,
							}}
						>
							{paymentTranslate('text.total')}:
						</Typography>
						<Typography
							sx={{
								fontSize: '20px',
								fontWeight: '700',
								color: titleTextColor,
							}}
						>
							$ {sliderValue * defaultDocumentPrice}
						</Typography>
					</Box>
				</Box>

				<Box
					className="not-rtl-able"
					sx={{
						width: { xs: '90%', sm: 'unset' },
						display: 'flex',
						justifyContent: { xs: 'space-around', sm: 'space-between' },
						gap: { xs: 0, sm: '16px' },
					}}
				>
					<Button
						sx={{
							height: '44px',
							width: { xs: '45%', sm: '215px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="outlined"
						color="primary"
						onClick={leavePaymentPage}
					>
						{paymentTranslate('buttons.cancel')}
					</Button>

					{connectingStripe ? (
						<Button
							sx={{
								height: '44px',
								width: { xs: '45%', sm: '215px' },
								borderRadius: '8px',
								marginTop: '24px',
							}}
							variant="outlined"
							color="primary"
							disabled={connectingStripe}
						>
							<Oval
								height={24}
								width={24}
								color="rgba(0, 0, 0, 0.26)"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="rgba(0, 0, 0, 0.26)"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Button>
					) : (
						<Button
							sx={{
								height: '44px',
								width: { xs: '45%', sm: '215px' },
								borderRadius: '8px',
								textTransform: 'none',
								fontSize: '16px',
								fontWeight: '700',
								marginTop: '24px',
							}}
							variant="contained"
							color="primary"
							disabled={!sliderValue || !connectionId}
							onClick={() => handlePayment(sliderValue)}
						>
							{paymentTranslate('buttons.confirm')}
						</Button>
					)}
				</Box>
			</Box>
		</SharedModal>
	);
};
